import React from 'react';
import { formatDateTime, formatMoney } from '../utils/helpers';

const STATUS_CONFIG = {
    draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
    pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending approval' },
    approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
    processed: { color: 'bg-purple-100 text-purple-800', label: 'Paid' },
    rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
};

const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    return (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${cfg.color}`}>
            {cfg.label}
        </span>
    );
};

const Line = ({ label, value }) => (
    <div className="flex justify-between">
        <span className="text-sm font-medium text-gray-700">{label}:</span>
        <span className="text-sm text-gray-900">{value}</span>
    </div>
);

const InvoiceDetail = ({ invoice, onClose, onDownload, downloading = false }) => {
    if (!invoice) {
        return (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-sm shadow-lg rounded-md bg-white text-center">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Not Found</h3>
                    <button onClick={onClose} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
                        Close
                    </button>
                </div>
            </div>
        );
    }

    const money = (v) => formatMoney(v, invoice?.currency);
    const feeLabel = invoice.commission_percent != null
        ? `Platform fee (${invoice.commission_percent}%)`
        : 'Platform fee';
    const feeEx = Number(invoice.platform_fee_ex_gst ?? 0);
    const showVendorFee = feeEx > 0 || Number(invoice.platform_fee_amount ?? 0) > 0;
    const gstPct = Math.round(Number(invoice.gst_rate || 0.15) * 100);

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-0 sm:top-10 mx-auto p-4 sm:p-6 border w-full sm:w-11/12 max-w-2xl shadow-lg rounded-none sm:rounded-md bg-white min-h-screen sm:min-h-0">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-6">
                    <div className="min-w-0">
                        <h2 className="text-xl md:text-2xl font-bold text-gray-900">Invoice #{invoice.id}</h2>
                        {invoice.service_number && (
                            <p className="text-gray-600 mt-1 text-sm">Service {invoice.service_number}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <StatusBadge status={invoice.status} />
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="card">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
                        <div className="space-y-3">
                            <Line label="Vendor" value={invoice.vendor_name || `#${invoice.vendor_id}`} />
                            <Line label="Case" value={invoice.case_id ?? '—'} />
                            <Line label="Rate source" value={invoice.rate_source || '—'} />
                            <Line label="Created" value={formatDateTime(invoice.created_at)} />
                            {invoice.confirmed_at && (
                                <Line label="Submitted" value={formatDateTime(invoice.confirmed_at)} />
                            )}
                            {invoice.processed_at && (
                                <Line label="Paid" value={formatDateTime(invoice.processed_at)} />
                            )}
                        </div>
                    </div>

                    <div className="card">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h3>
                        <div className="space-y-3">
                            <Line label="Service rate (ex GST)" value={money(invoice.rate_ex_gst ?? invoice.rate_amount)} />
                            {showVendorFee && (
                                <Line
                                    label={feeLabel}
                                    value={`- ${money(invoice.platform_fee_ex_gst ?? invoice.platform_fee_amount)}`}
                                />
                            )}
                            {Number(invoice.extra_charges_amount || invoice.extra_ex_gst || 0) > 0 && (
                                <Line
                                    label="Extra charges"
                                    value={money(invoice.extra_charges_amount ?? invoice.extra_ex_gst)}
                                />
                            )}
                            <Line
                                label="Total"
                                value={money(invoice.amount_after_fee_ex_gst ?? invoice.gross_ex_gst)}
                            />
                            <Line label={`GST (${gstPct}%)`} value={money(invoice.gst_amount ?? 0)} />
                            <hr />
                            <div className="flex justify-between text-lg">
                                <span className="font-medium text-gray-700">Net payable:</span>
                                <span className="font-bold text-gray-900">{money(invoice.net_amount)}</span>
                            </div>
                            {invoice.extra_charges_notes && (
                                <p className="text-xs text-gray-500 pt-1">Note: {invoice.extra_charges_notes}</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                    <button onClick={onClose} className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
                        Close
                    </button>
                    {invoice.pdf_path && (
                        <button
                            onClick={() => onDownload(invoice.id)}
                            disabled={downloading}
                            className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {downloading ? 'Downloading…' : 'Download PDF'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InvoiceDetail;
